/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/Layout/Sidebar.tsx
 * DESCRIPTION: Main navigation sidebar component for the VeriMark
 *              platform. Provides persistent navigation across all
 *              authenticated pages with organization context, user
 *              profile access, and admin submenu.
 *
 * FEATURES:
 * - Dynamic organization display and switching context
 * - Navigation items with active state highlighting
 * - User profile section with sign-out functionality
 * - Settings submenu with admin-only pages (lock protected)
 * - Responsive design optimized for desktop layout
 * - Icon-based navigation using Lucide React icons
 * - Dark mode support
 *
 *  * UPDATES: Main navigation sidebar with mobile responsiveness,
 *              collapsible menu, and custom scrollbar styling.

 *
 * NAVIGATION STRUCTURE:
 * 1. Dashboard - Overview and quick stats
 * 2. Analyze File - Upload and analyze media files
 * 3. Library - Media library and identity management
 * 4. Identities - File identity details
 * 5. AI Providers - AI provider configuration
 * 6. Organizations - Manage multiple organization contexts
 * 7. Billing & Plans - Subscription management
 * 8. Audit Logs - API keys, webhooks, and activity tracking
 * 9. Settings > Admin (Analytics, AI Providers, User Management)
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.0.0
 * @created 2025-01-15
 * @updated 2026-03-27 - Added Settings submenu with admin pages,
 *                       dark mode support, removed Team page
 * @updated 2026-04-03 - Added mobile collapsible menu, hamburger toggle,
 *                       custom scrollbar, overlay for mobile
 * ─────────────────────────────────────────────────────────────────────
 */

import { NavLink, useNavigate } from 'react-router-dom';
import { useUser, useOrganization, useClerk } from '@clerk/react';
import {
  LayoutDashboard,
  Upload,
  Building2,
  CreditCard,
  FileText,
  TrendingUp,
  LogOut,
  ChevronDown,
  FolderOpen,
  Cpu,
  Settings,
  Shield,
  Lock,
  IdCard,
  Menu,
  X,
  Activity,
} from 'lucide-react';
import { useState } from 'react';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
}

interface AdminMenuItem {
  to: string;
  icon: React.ElementType;
  label: string;
}

const Sidebar: React.FC = () => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navItems: NavItem[] = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/analyze', icon: Upload, label: 'Analyze File' },
    { to: '/library', icon: FolderOpen, label: 'Library' },
    { to: '/identities', icon: IdCard, label: 'Identities' },
    { to: '/ai-providers', icon: Cpu, label: 'AI Providers' },
    { to: '/organizations', icon: Building2, label: 'Organizations' },
    { to: '/billing', icon: CreditCard, label: 'Billing & Plans' },
    { to: '/audit', icon: FileText, label: 'Audit Logs' },
  ];

  const adminMenuItems: AdminMenuItem[] = [
    { to: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
    { to: '/admin/ai-providers', icon: Cpu, label: 'AI Providers' },
    { to: '/admin/users', icon: Shield, label: 'User Management' },
    { to: '/admin/telemetry', icon: Activity, label: 'Telemetry' },
  ];

  const handleSignOut = () => {
    signOut(() => {
      window.location.href = '/login';
    });
  };

  // ─────────────────────────────────────────────────────────────────────
  // SIDEBAR - THEME-AWARE VERSION
  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
    {/* Mobile Hamburger Toggle */}
    <button
    onClick={() => setIsMobileOpen(!isMobileOpen)}
    className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
    >
    {isMobileOpen ? (
      <X className="w-6 h-6 text-gray-900 dark:text-white" />
    ) : (
      <Menu className="w-6 h-6 text-gray-900 dark:text-white" />
    )}
    </button>

    {/* Mobile Overlay */}
    {isMobileOpen && (
      <div
      className="lg:hidden fixed inset-0 bg-black/50 z-40"
      onClick={() => setIsMobileOpen(false)}
      />
    )}

    {/* Sidebar */}
    <aside
    className={`
      w-[250px] bg-white dark:bg-slate-900 text-gray-900 dark:text-white
      flex flex-col fixed h-full z-40
      transform transition-transform duration-300 ease-in-out
      lg:translate-x-0
      ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
      border-r border-gray-200 dark:border-slate-800
      `}
      >
      {/* Custom Scrollbar Styles */}
      <style>{`
        .sidebar-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .sidebar-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        .dark .sidebar-scroll::-webkit-scrollbar-track {
          background: #1e293b;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        .dark .sidebar-scroll::-webkit-scrollbar-thumb {
          background: #475569;
        }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
        .dark .sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
        `}</style>

        {/* Logo */}
        <div className="p-6 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
        <Cpu className="w-5 h-5 text-white" />
        </div>
        <span className="text-xl font-semibold text-gray-900 dark:text-white">VeriMark</span>
        </div>
        </div>

        {/* Organization Context */}
        {organization && (
          <div className="p-4 border-b border-gray-200 dark:border-slate-800">
          <NavLink
          to="/organizations"
          onClick={() => setIsMobileOpen(false)}
          className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
          <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {organization.name}
          </p>
          <p className="text-xs text-gray-600 dark:text-slate-400">Organization</p>
          </div>
          </div>
          <ChevronDown className="w-4 h-4 text-gray-600 dark:text-slate-400" />
          </NavLink>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto sidebar-scroll">
        {navItems.map((item) => (
          <NavLink
          key={item.to}
          to={item.to}
          onClick={() => setIsMobileOpen(false)}
          className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
            isActive
            ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white'
            : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
          }`
          }
          >
          <item.icon className="w-5 h-5" />
          <span className="text-sm font-medium">{item.label}</span>
          </NavLink>
        ))}

        {/* Settings with Admin Submenu */}
        <div>
        <NavLink
        to="/settings"
        onClick={(e) => {
          e.preventDefault();
          setIsSettingsOpen(!isSettingsOpen);
        }}
        className={({ isActive }) =>
        `flex items-center justify-between px-4 py-3 rounded-lg transition-colors w-full ${
          isActive || isSettingsOpen
          ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white'
          : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
        }`
        }
        >
        <div className="flex items-center gap-3">
        <Settings className="w-5 h-5" />
        <span className="text-sm font-medium">Settings</span>
        </div>
        <ChevronDown
        className={`w-4 h-4 transition-transform ${
          isSettingsOpen ? 'rotate-180' : ''
        }`}
        />
        </NavLink>

        {/* Admin Submenu */}
        {isSettingsOpen && (
          <div className="ml-4 mt-1 space-y-1">
          {adminMenuItems.map((item) => (
            <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setIsMobileOpen(false)}
            className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm ${
              isActive
              ? 'bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-white'
              : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700'
            }`
            }
            >
            <item.icon className="w-4 h-4" />
            <span>{item.label}</span>
            <Lock className="w-3 h-3 ml-auto text-gray-400 dark:text-slate-500" />
            </NavLink>
          ))}
          </div>
        )}
        </div>
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-800">
        <NavLink
        to="/profile"
        onClick={() => setIsMobileOpen(false)}
        className="flex items-center gap-3 mb-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
        {user?.imageUrl ? (
          <img
          src={user.imageUrl}
          alt={user.firstName || 'User'}
          className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
          {user?.firstName?.charAt(0) || user?.emailAddresses[0]?.emailAddress?.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
        {user?.firstName || user?.emailAddresses[0]?.emailAddress}
        </p>
        <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
        {user?.emailAddresses[0]?.emailAddress}
        </p>
        </div>
        </NavLink>
        <button
        onClick={handleSignOut}
        className="w-full flex items-center gap-3 px-4 py-2 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
        <LogOut className="w-5 h-5" />
        <span className="text-sm font-medium">Sign Out</span>
        </button>
        </div>
        </aside>
        </>
  );
};

export default Sidebar;
